'use client'

import { Dialog, DialogPanel } from '@headlessui/react'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { type ReactNode } from 'react'

import { ModalBackdrop } from '@src/components/Modal/ModalBackdrop'
import { filterBuilderAttributes } from '@src/utils/builder.util'
import { cn } from '@src/utils/tailwind'

export interface ModalContentProps {
  isOpen: boolean
  onClose: () => void
  children: ReactNode
  title?: string
  size?: 'small' | 'medium' | 'large'
  position?: 'center' | 'bottom'
  attributes?: any
  isContentHidden?: boolean
  isBackdropHidden?: boolean
  isFullScreenMobile?: boolean
  dialogClassName?: string
}

export const ModalContent = (props: ModalContentProps) => {
  const {
    children,
    isOpen,
    onClose,
    attributes,
    position,
    size,
    title,
    isContentHidden,
    isBackdropHidden,
    isFullScreenMobile = true,
    dialogClassName,
  } = props
  const { className: attributesClassName, ...rest } = filterBuilderAttributes(attributes)

  return (
    <Dialog open={isOpen} onClose={onClose} className={cn('relative z-40', dialogClassName)}>
      {!isBackdropHidden && <ModalBackdrop />}

      <div
        className={cn(
          'visible fixed inset-0 z-40 scale-100 overflow-y-auto opacity-100 transition-all duration-200 lg:p-10',
          {
            'flex items-center': position === 'center',
            'invisible opacity-0 scale-95': isContentHidden,
          }
        )}
      >
        <DialogPanel
          transition
          className={cn(
            'relative mx-auto overflow-auto rounded-3xl bg-white shadow-md transition-all lg:min-h-px',
            'max-w-[calc(100%-2rem)] p-6 lg:max-w-[932px] lg:p-16',
            'data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-150 data-enter:ease-out data-leave:duration-150 data-leave:ease-in data-closed:lg:translate-y-0 data-closed:lg:scale-95',
            { 'lg:max-w-[638px]': size === 'medium' },
            { 'lg:max-w-[932px]': size === 'large' },
            {
              'w-full max-w-screen min-h-svh max-h-svh rounded-none lg:rounded-3xl px-4 py-14':
                isFullScreenMobile,
            },
            attributesClassName
          )}
          {...rest}
        >
          <div
            className={cn('flex items-center justify-between gap-1 text-bodi-black-text', {
              'mb-6': !!title,
            })}
          >
            {title && <h2 className="text-heading-display-3">{title}</h2>}
            <button
              onClick={onClose}
              className={cn(
                'absolute top-4 right-4 lg:top-5 lg:right-5',
                'flex items-center justify-center rounded-full bg-gray-100 p-1 text-gray-700 transition-all hover:bg-gray-200'
              )}
            >
              <XMarkIcon className="size-6 stroke-[2.5px]" />
            </button>
          </div>
          {children}
        </DialogPanel>
      </div>
    </Dialog>
  )
}
